import type { Partner } from "@expert/shared-types";
import type React from "react";

import { freezeAppConfigSingleton, useAppConfigAndServiceProviderOverride } from "@expert/app-config";
import { useNotification } from "@expert/common-ui";
import { partnerEnabledForExpertUniversity } from "@expert/expert-university";
import { GaiaWebsocketProvider } from "@expert/gaia";
import { enableWorkerLogging, getLogger } from "@expert/logging";
import { getAllowedPartnersOverride, getServiceProvider } from "@expert/service-providers";
import { ExpertWorkspaceWebSocketProvider } from "@expert/websocket";
import { Button, Flex, Group } from "@mantine/core";
import { useNetwork } from "@mantine/hooks";
import { useEffect, useMemo, useRef } from "react";
import { withErrorBoundary } from "react-error-boundary";

import { ConnectCallbackTimeoutError } from "@/amazon-connect/withCallbacks";
import { useAnalytics } from "@/analytics";
import { useSetupSsoToken } from "@/auth";
import { GlobalError } from "@/common-ui";
import { WorkspaceLoader } from "@/common-ui/WorkspaceLoader";
import { Workspace } from "@/components";
import EvlConnectionNotifications from "@/components/evl/EvlConnectionNotifications";
import { env } from "@/config";
import { getBackendUrls, shouldUseK8s } from "@/config/getBackendUrls";
import { useHostingEnvironment } from "@/config/hostingEnvStore";
import { WorkspaceFeatureFlags } from "@/features";
import { useAgentAttributesStore } from "@/sales-tools/stores/agent-attributes.store";
import { AgentSdkProvider } from "@/sdk/agent/AgentSdkProvider";
import { getAgentLocation } from "@/sdk/agent/utils/getAgentLocation";
import { useLogger } from "@/sdk/logging/LoggerProvider";
import { createFallbackRender, errorEventBus, getSharedApiClient, useFeatureFlag, userCache } from "@/shared-utils";
import { USER_FRIENDLY_NAME_QS_KEY } from "@/shared-utils/constants";

const logger = getLogger({ module: "Home" });

const WorkspaceHandled = withErrorBoundary(Workspace, {
    fallbackRender: createFallbackRender(logger),
});

export default function Home(): React.JSX.Element {
    const logger = useLogger({ module: "Home" });
    const { data: isEvlConnectionNotificationsEnabled = false } = useFeatureFlag(
        "EWP-isEvlConnectionNotificationsEnabled",
    );
    const environment = import.meta.env.VITE_ENV_TYPE as string;
    const employeeId = userCache.employeeId ?? undefined;
    const userFriendlyName =
        environment === "development"
            ? (new URLSearchParams(window.location.search).get(USER_FRIENDLY_NAME_QS_KEY) ?? undefined)
            : undefined;
    const allowedPartnersOverride = getAllowedPartnersOverride();
    const notification = useNotification();
    const { dispatcher } = useAnalytics();
    const auth = useSetupSsoToken(userFriendlyName);
    const { data: isWebsocketEnabled = true, isLoading: isLoadingWebsocketEnabled } = useFeatureFlag(
        "EWP-isWebsocketEnabled",
        {
            org: auth.user?.profile.org,
        },
    );
    const { data: isInExpertUniversityPilot } = useFeatureFlag("isInExpertUniversityPilot");
    const { data: isMorningPaperEnabled = false, isLoading: isLoadingMorningPaperEnabled } =
        useFeatureFlag<boolean>("isMorningPaperEnabled");

    const hostingEnv = useHostingEnvironment(employeeId, env.channel);

    // TODO: Remove Twilio from here
    const {
        data: [appConfig, serviceProviderOverride] = [],
        isLoading: appConfigLoading,
        error: appConfigError,
        status: appConfigAndServiceProviderOverrideStatus,
    } = useAppConfigAndServiceProviderOverride(getSharedApiClient(), employeeId, env.channel);

    const partner = allowedPartnersOverride?.length
        ? allowedPartnersOverride[0]
        : (appConfig?.allowedPartners.split(",")[0] as Partner | undefined);

    /**
     * Priority order is:
     * 1. `ServiceProviderOverride` feature flag
     * 2. `serviceProvider` query param (nonprod only)
     * 3. `CLOUD_SPECIFIC_SERVICE_PROVIDER` env var (chat only)
     * 4. static provider mapping by partner
     * 5. `TWILIO` fallback
     */
    const serviceProvider = useMemo(() => {
        return serviceProviderOverride ?? getServiceProvider(partner, env.channel);
    }, [partner, serviceProviderOverride]);

    const isTrainingModeAllowed =
        serviceProvider === "AMAZON_CONNECT" &&
        partner &&
        partnerEnabledForExpertUniversity(partner, env.channel, isInExpertUniversityPilot);

    useEffect(() => {
        /**
         * Wait for feature flag to finish loading before saving resolved service providers to localStorage
         * This is to avoid briefly setting the wrong service provider in localStorage during the loading process.
         */
        if (appConfigAndServiceProviderOverrideStatus === "success" && env.channel === "chat") {
            localStorage.setItem("serviceProvider", serviceProvider);
        }
    }, [appConfigAndServiceProviderOverrideStatus, serviceProvider]);

    const partnerConfig =
        appConfig && partner && appConfig.partnersConfig[partner in appConfig.partnersConfig ? partner : "others"];

    useEffect(() => {
        const errorUnsub = errorEventBus.on("error-occurred", (error) => {
            logger.info({ error }, "Error notification shown in UI.");

            if (error instanceof ConnectCallbackTimeoutError) {
                notification.error(
                    "The call is taking longer than expected to complete. Please refresh the page and try again.",
                );
            } else if (error instanceof Error) {
                notification.error(`An unexpected error occurred: ${error.message}`);
            } else {
                notification.error("An unexpected error occurred");
            }
        });

        return () => {
            errorUnsub();
        };
    }, [logger, notification]);

    useFeatureFlag("EWP-isWorkerLoggingEnabled", undefined, {
        onResponse: (isWorkerLoggingEnabled = false) => {
            if (isWorkerLoggingEnabled) enableWorkerLogging();
        },
    });

    const handlePageReload = () => {
        localStorage.clear();
        sessionStorage.clear();
        window.location.reload();
    };

    useEffect(() => {
        const getAgentSite = async () => {
            const { locationId: site } = await getAgentLocation(logger);
            useAgentAttributesStore.getState().setAgentSite(site);
        };
        getAgentSite();
    }, [logger]);

    useEffect(() => {
        if (!auth.user && auth.isLoading) {
            void dispatcher.dispatchBusinessEvent("Authenticating");
        }
    }, [dispatcher, auth.user, auth.isLoading]);

    // normally, `useState()` is desirable but in this case we need to change the value and read it before React rerenders
    const isAuthRedirectNeededRef = useRef(false);
    function handleOnAuthRedirect() {
        logger.warn("Redirecting for agent sdk re-authentication");
        isAuthRedirectNeededRef.current = true;
    }

    const networkStatus = useNetwork();
    useEffect(() => {
        logger.info(networkStatus, "Network status");
    }, [logger, networkStatus]);

    if (!auth.error && auth.isLoading) {
        return <WorkspaceLoader msg="Acquiring access token..." />;
    }

    if (appConfigLoading || hostingEnv.isLoading || isLoadingWebsocketEnabled || isLoadingMorningPaperEnabled) {
        return <WorkspaceLoader msg="Checking if experimental features should be enabled..." />;
    }

    if (auth.error || !auth.user) {
        logger.fatal({ error: auth.error }, "Error authenticating");
        return (
            <GlobalError msg="You can also contact Service Desk for help or if it’s your first time using Expert Workspace.">
                <Group>
                    <Button mt="sm" onClick={handlePageReload} variant="outline" c="white" color="asurion.5">
                        Refresh
                    </Button>
                </Group>
            </GlobalError>
        );
    }

    if (!appConfigError && (appConfigLoading || !appConfig)) {
        void dispatcher.dispatchAction("LoadingConfiguration", "LoadingConfiguration");
        return <WorkspaceLoader msg="Loading configuration..." />;
    }

    if ((!appConfigLoading && !appConfig) || appConfigError) {
        logger.fatal({ error: appConfigError }, "Error loading configuration");
        return (
            <GlobalError title="Error loading Workspace configuration">
                <Button mt="sm" onClick={handlePageReload}>
                    Refresh
                </Button>
            </GlobalError>
        );
    }

    if (hostingEnv.isError) {
        logger.fatal({ error: hostingEnv.error }, "Error loading hosting environment");
        return (
            <GlobalError title="Error loading Workspace configuration">
                <Button mt="sm" onClick={handlePageReload}>
                    Refresh
                </Button>
            </GlobalError>
        );
    }

    freezeAppConfigSingleton({ appConfig: appConfig!, org: userCache.org, channel: env.channel });

    const { ssoAccessToken, identity } = userCache;
    if (!ssoAccessToken) return <h1>DEBUG: NO token</h1>;
    if (!identity) return <h1>DEBUG: NO identity</h1>;
    if (!employeeId) {
        logger.fatal({ userCache }, "No employee ID for user.");
        return <h1>DEBUG: NO employee ID</h1>;
    }

    return (
        <Flex flex={1} direction="column" h="100%" w="100%">
            {/* TODO: Move relevant providers up once we have several pages */}
            <ExpertWorkspaceWebSocketProvider
                identity={employeeId}
                token={ssoAccessToken}
                webSocketUrl={getBackendUrls().expertWorkspaceWebSocketUrl}
                useSocketIO={shouldUseK8s() || env.useSocketIO}
                isWebsocketEnabled={isWebsocketEnabled}
            >
                <AgentSdkProvider
                    employeeNum={employeeId}
                    userFriendlyName={userFriendlyName}
                    serviceProvider={serviceProvider}
                    partnerConfig={partnerConfig}
                    onAuthRedirect={handleOnAuthRedirect}
                    isTrainingModeAllowed={isTrainingModeAllowed}
                >
                    {env.channel !== "chat" && isEvlConnectionNotificationsEnabled && <EvlConnectionNotifications />}
                    {/* TODO: Get client from the worker attributes maybe? */}
                    <GaiaWebsocketProvider
                        identity={identity}
                        application="expert-workspace"
                        token={ssoAccessToken}
                        url={env.gaiaWebsocketUrl}
                        logger={logger}
                    >
                        <WorkspaceFeatureFlags employeeId={employeeId} logger={logger}>
                            <WorkspaceHandled
                                isAuthRedirectNeededRef={isAuthRedirectNeededRef}
                                isMorningPaperEnabled={isMorningPaperEnabled}
                            />
                        </WorkspaceFeatureFlags>
                    </GaiaWebsocketProvider>
                </AgentSdkProvider>
            </ExpertWorkspaceWebSocketProvider>
        </Flex>
    );
}
